/* eslint-disable react-hooks/exhaustive-deps */
// src/pages/seller/Seller2Net/Seller2Net.js
import { useState, useContext, useEffect, useRef, useCallback } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { useModal } from "../../../context/ModalContext";
import { ThemeContext } from "../../../context/ThemeContext";
// import DynamicBackButton from "../../../components/backbutton/BackButton";
import AddressSearchPreview from "../AddressSearchPreview";
import { formatCurrency } from "../../../utils/format";
import PropertyDetailsSection from "./components/PropertyDetailsSection";
import ManualSheetSection from "./components/ManualSheetSection";
import ResultsModal from "./components/ResultsModal";
import { useUserTemplate, loadUserTemplate } from "./hooks/useUserTemplate";
import { useCurrencyFormat } from "./hooks/useCurrencyFormat";
import "../../../calculater.css";
import "../../../responsiveness.css";
import FloatingInput from "../../../components/input/FloatingInput";
import FloatingNumber from "../../../components/input/FloatingNumber";
import CustomSelect from "../../../components/customselect/CustomSelect";
import {
  getSellerEstimatedTax,
  computeAllTotals,
  updateDerivedFields,
  calculateAdjusted,
} from "./utils/calculations";

import {
  getTitleEscrowCosts,
  getStateSettings,
  getNationalSettings,
} from "./services/titleEscrowService";
// import { getNationalSettingService } from "./services/nationalSettingService";
import { CommonHeader } from "../../../components/ui/CommonHeader";
import { saveNetFirstCalc, updateNetFirstCalc, getNetFirstCalc, getUserTemplate } from "./services/sellerNetService";
// import { set } from "date-fns";
import { showInvalidAddressToast } from "./toasts";
import Toast from "../../../components/common/Toast";
import { generatePdf } from "../PdfRouteService";
// import { fetchPrintDisclaimerForPdf } from "../../../utils/disclaimerApi";
import { buildSellerNetPayload } from "../../../utils/payloadBuilder";

const SellerNetSheet = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { openModal, closeModal, closeAllModals } = useModal();
  const theme = useContext(ThemeContext);

  const [searchParams] = useSearchParams();
  const isQuickMode = searchParams.get("mode") === "quick";

  const preservedAddressFromNav = location?.state?.preservedAddressMeta;
  const PRESERVED_ADDRESS_STORAGE_KEY = "seller2net_preservedAddressMeta";
  const PRESERVED_LOAN_INFO_STORAGE_KEY = "seller2net_preservedLoanInfoMeta";
  const PRESERVED_ANNUAL_PROPERTY_TAX_STORAGE_KEY =
    "seller2net_preservedAnnualPropertyTax";
  const PRESERVED_SELLER_OTHER_COSTS_STORAGE_KEY =
    "seller2net_preservedSellerOtherCostMeta";
  const PRESERVED_TEMPLATE_SELLER_OTHER_COSTS_STORAGE_KEY =
    "seller2net_preservedTemplateSellerOtherCosts";

  const mainId = theme?.id || "";
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const userId = user?.id;

  // const [step, setStep] = useState(1);
  const [companyId, setCompanyId] = useState(user?.companyid || null);
  const [main_company_name, setMainCompanyId] = useState(
    theme?.main_company_name || null
  );

  const [toast, setToast] = useState({
    message: "",
    visible: false,
    type: "success",
  });

  const [templateMissing, setTemplateMissing] = useState(false);
  const showToasterCallback = useCallback(
    ({ message, visible, type = "success" }) => {
      try {
        setToast({ message, visible, type });
        setTimeout(
          () => setToast({ message: "", visible: false, type: "success" }),
          5000
        );
      } catch (e) {
        console.warn("showToaster failed", e);
      }
    },
    [setToast]
  );

  const [ttbData, setTtbData] = useState({});
  // mapped national settings used for calculator adjustments
  const [nationalSettingsMap, setNationalSettingsMap] = useState({});
  // const [salesPriceError, setSalesPriceError] = useState("");
  // const [mortgageAmountError, setMortgageAmountError] = useState("");
  const salesPriceError = "";
  const mortgageAmountError = "";
  const [isCorrectProperty, setIsCorrectProperty] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [showPreviewFromChild, setShowPreviewFromChild] = useState(false);
  // control whether the ManualSheetSection is shown directly in the page
  const [showManualEditor, setShowManualEditor] = useState(false);
  const [showAddressView, setShowAddressView] = useState(true);
  const [useMortgageBalance, setUseMortgageBalance] = useState(true);
  const [useAnnualPropertyTax, setUseAnnualPropertyTax] = useState(true);
  const [useHomeWarranty, setUseHomeWarranty] = useState(false);

  // After calculation completes, prevent editing calculator inputs until the user
  // explicitly starts a new calculation.
  const [calcCompleted, setCalcCompleted] = useState(false);
  const calcCompletedRef = useRef(calcCompleted);
  useEffect(() => {
    calcCompletedRef.current = calcCompleted;
  }, [calcCompleted]);
  const startNewCalcPromptOpenRef = useRef(false);

  // new states
  const [initialSellerMust, setInitialSellerMust] = useState(0);
  const initialSellerMustRef = useRef(initialSellerMust);
  const setInitialSellerMustAndRef = (val) => {
    setInitialSellerMust(val);
    initialSellerMustRef.current = val;
  };
  const inCalcRef = useRef(false);

  // Allows callers (like runCalculation) to await the completion of the
  // net-first iteration loop (calSellerWant -> calculateClosingCosts -> handleSellerCalculation ...).
  const netFirstAwaitRef = useRef({
    runId: 0,
    resolve: null,
    timeoutId: null,
  });

  const startAwaitNetFirstCompletion = useCallback((timeoutMs = 20000) => {
    try {
      if (netFirstAwaitRef.current?.timeoutId)
        clearTimeout(netFirstAwaitRef.current.timeoutId);
    } catch (e) {
      /* ignore */
    }

    const runId = (netFirstAwaitRef.current?.runId || 0) + 1;
    netFirstAwaitRef.current.runId = runId;

    return new Promise((resolve) => {
      // Wrapper so we only resolve the *current* run.
      netFirstAwaitRef.current.resolve = (payload = {}) => {
        try {
          if (netFirstAwaitRef.current?.runId !== runId) return;
          if (netFirstAwaitRef.current?.timeoutId)
            clearTimeout(netFirstAwaitRef.current.timeoutId);
        } catch (e) {
          /* ignore */
        }
        netFirstAwaitRef.current.timeoutId = null;
        netFirstAwaitRef.current.resolve = null;
        resolve({ runId, ...payload });
      };

      // Safety: don't hang the UI forever if something breaks the loop.
      try {
        netFirstAwaitRef.current.timeoutId = setTimeout(() => {
          try {
            if (netFirstAwaitRef.current?.runId !== runId) return;
            if (typeof netFirstAwaitRef.current?.resolve === "function") {
              netFirstAwaitRef.current.resolve({ status: "timeout" });
            }
          } catch (e) {
            resolve({ runId, status: "timeout" });
          }
        }, timeoutMs);
      } catch (e) {
        // If setTimeout fails for some reason, resolve immediately.
        resolve({ runId, status: "no-timeout" });
      }
    });
  }, []);

  const resolveAwaitNetFirstCompletion = useCallback((payload = {}) => {
    try {
      if (typeof netFirstAwaitRef.current?.resolve === "function") {
        netFirstAwaitRef.current.resolve(payload);
      }
    } catch (e) {
      /* ignore */
    }
  }, []);
  const [maintainAmount, setMaintainAmount] = useState(0);
  const maintainAmountRef = useRef(maintainAmount);
  const setMaintainAmountAndRef = (val) => {
    setMaintainAmount(val);
    maintainAmountRef.current = val;
  };

  const [, setCounter] = useState(0);

  const [netfirstDone, setNetfirstDone] = useState(false);
  //const netfirstDone = false;
  const lastFACCRequestRef = useRef({ salePrice: 0.0, adjusted: 0.0 });

  const cleanNumber = (v) => {
    if (v === undefined || v === null) return "";
    const s = typeof v === "string" ? v : String(v);
    return s.replace(/[^0-9.]/g, "");
  };

  const parseCurrency = (val) => {
    const n = Number(String(val ?? "").replace(/[^0-9.-]+/g, ""));
    return Number.isFinite(n) ? n : 0;
  };

  // helper to read current form values when migrating off legacy `formData` prop
  const getVal = (path, fallback = "") => {
    try {
      if (typeof getValues === "function") {
        const v = getValues(path);
        return v === undefined ? fallback : v;
      }
    } catch (e) {
      // ignore and return fallback
    }
    return fallback;
  };

  // Initial RHF default values (canonical shape). We'll progressively
  // migrate state into react-hook-form; start by seeding useForm with
  // this object so children can consume RHF-only values via control/useFieldArray.
  const initialValues = {
    closingCostMeta: [],
    sellerClosingCostMeta: [],
    sellerOtherCostMeta: [],
    compensationMeta: [],
    otherCostMeta: [],
    // Keep blank by default so a fresh calculator doesn't show 0.
    sellerMustNet: 0,
    totalClosingMeta: {
      totalAllCostsAndCredits_total: 0,
      totalAllPaymentToBuyer_total: 0,
      totalClosingCosts_total: 0,
      totalEstimatedCashToSeller: 0,
    },
    addressMeta: {},
    loanInfoMeta: {
      adjusted: 0,
      amount: 0,
      annualPropertyTax: 0,
      assessorsTaxRate: 0,
      autoEstimateLoan: false,
      buyersLoanType: "Conventional",
      buyerDownPayment: 0,
      conventionalPercent: 80,
      estimatedTaxProrations: 0,
      loansToBePaidBalance1: 0,
      loansToBePaidRate1: 0,
      loansToBePaidBalance2: 0,
      loansToBePaidRate2: 0,
      loansToBePaidBalance3: 0,
      loansToBePaidRate3: 0,
      transferTaxRatePer: 0,
    },
    preparedBy: "",
    preparedFor: "New Client",
    salesPrice: "0.00",
    titleSettlementMeta: {
      ownersTitleAmount: "0.00",
      ownersTitleSeller: "0.00",
      ownersTitlePayor: "Seller",

      escrowSettlementPayor: "Seller",
      escrowSettlementAmount: "0.00",
      escrowSettlementSeller: "0.00",
      escrowSettlementSplit: "0.00",

      lendersTitleAmount: "0.00",
      lendersTitleSeller: "0.00",
      lendersTitlePayor: "Seller",
    },
  };

  // Create a single react-hook-form instance at the parent so child components
  // can share the same form methods. We'll seed it with the canonical defaults.
  const formMethods = useForm({ defaultValues: initialValues });
  const { control, watch, setValue, getValues } = formMethods;

  // Instead of logging on every keystroke via watch (too noisy), listen
  // for focusout events on the main container and log the full form values
  // when an input loses focus. This avoids per-key logging and doesn't
  // require modifying individual input components.
  const containerRef = useRef(null);

  const openStartNewCalculationPrompt = useCallback(() => {
    try {
      if (startNewCalcPromptOpenRef.current) return;
      startNewCalcPromptOpenRef.current = true;

      openModal({
        title: "Warnings",
        size: "smallmodel",
        onClose: () => {
          // Make the close icon behave like clicking "No"
          try {
            startNewCalcPromptOpenRef.current = false;
          } catch (e) {
            /* ignore */
          }
        },
        content: (
          <div className="modelspace pplication-fe">
            <p className="model-desc-details notice-icon">
              Any customization requires starting a new calculation before calculating the Seller Must Net.<br />
              Do you want to start a new calculation now?
            </p>
            <div className="frm-btn popup-2-btn">
              <button
                className="submit-btn btn-bg inpt-btn btn-discard"
                onClick={() => {
                  try {
                    closeModal();
                  } finally {
                    startNewCalcPromptOpenRef.current = false;
                  }
                }}
              >
                Cancel
              </button>
              <button
                className="submit-btn btn-bg inpt-btn"
                onClick={async () => {
                  try {
                    closeAllModals();
                  } catch (e) {
                    /* ignore */
                  }

                  try {
                    startNewCalcPromptOpenRef.current = false;
                    setCalcCompleted(false);

                    // Reset internal refs/state so the next run is clean.
                    try {
                      initialSellerMustRef.current = 0;
                      setInitialSellerMust(0);
                    } catch (e) {
                      /* ignore */
                    }
                    try {
                      maintainAmountRef.current = 0;
                      setMaintainAmount(0);
                    } catch (e) {
                      /* ignore */
                    }
                    try {
                      setCounter(0);
                    } catch (e) {
                      /* ignore */
                    }
                    try {
                      inCalcRef.current = false;
                    } catch (e) {
                      /* ignore */
                    }
                    try {
                      lastFACCRequestRef.current = { salePrice: 0.0, adjusted: 0.0 };
                    } catch (e) {
                      /* ignore */
                    }

                    await handleNewCalc({ forceQuick: true });
                  } catch (e) {
                    // If anything fails, at least allow the user to interact again.
                    startNewCalcPromptOpenRef.current = false;
                    setCalcCompleted(false);
                  }
                }}
              >
                Start New Calculation
              </button>
            </div>
          </div>
        ),
      });
    } catch (e) {
      startNewCalcPromptOpenRef.current = false;
    }
  }, [closeAllModals, closeModal, openModal]);

  // Once calculation is completed, intercept focus into any editable field and ask
  // whether to start a new calculation.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const isEditableTarget = (t) => {
      try {
        if (!t) return false;
        // Our dropdowns are often custom (see CustomSelect) and don't use a native <select>.
        // Treat any interaction within .custom-select as an editable intent.
        try {
          if (typeof t.closest === "function") {
            if (t.closest(".custom-select")) return true;
          }
        } catch (e) {
          // ignore
        }

        const tag = (t.tagName || "").toString().toLowerCase();
        if (tag === "input" || tag === "textarea" || tag === "select") return true;
        if (t.isContentEditable) return true;

        // Fallback for select-like widgets.
        try {
          const role = (t.getAttribute && t.getAttribute("role")) || "";
          const r = role.toString().toLowerCase();
          if (r === "combobox" || r === "listbox" || r === "option") return true;
        } catch (e) {
          // ignore
        }
      } catch (e) {
        // ignore
      }
      return false;
    };

    const onFocusIn = (e) => {
      try {
        if (!calcCompletedRef.current) return;
        const target = e?.target;
        if (!isEditableTarget(target)) return;

        // Prevent any interaction with the field.
        try {
          target && target.blur && target.blur();
        } catch (err) {
          /* ignore */
        }
        try {
          e.preventDefault && e.preventDefault();
          e.stopPropagation && e.stopPropagation();
        } catch (err) {
          /* ignore */
        }

        openStartNewCalculationPrompt();
      } catch (err) {
        // ignore
      }
    };

    // Some custom selects open on pointer/mouse down without reliably firing focus events.
    // Capture those interactions as well.
    const onPointerDown = (e) => {
      try {
        if (!calcCompletedRef.current) return;
        const target = e?.target;
        if (!isEditableTarget(target)) return;

        try {
          target && target.blur && target.blur();
        } catch (err) {
          /* ignore */
        }
        try {
          e.preventDefault && e.preventDefault();
          e.stopPropagation && e.stopPropagation();
        } catch (err) {
          /* ignore */
        }

        openStartNewCalculationPrompt();
      } catch (err) {
        // ignore
      }
    };

    el.addEventListener("focusin", onFocusIn, true);
    el.addEventListener("pointerdown", onPointerDown, true);
    // Fallback for environments that don't emit pointer events.
    el.addEventListener("mousedown", onPointerDown, true);
    return () => {
      el.removeEventListener("focusin", onFocusIn, true);
      el.removeEventListener("pointerdown", onPointerDown, true);
      el.removeEventListener("mousedown", onPointerDown, true);
    };
  }, [openStartNewCalculationPrompt]);

  // Currency formatting hook: prefer RHF setValue when available
  const handleCurrencyBlur = useCurrencyFormat(undefined, undefined, setValue);

  const sellerClosingCostArray = useFieldArray({
    control,
    name: "sellerClosingCostMeta",
  });
  const closingCostsSection2Array = useFieldArray({
    control,
    name: "closingCostMeta",
  });
  const sellerOtherCostArray = useFieldArray({
    control,
    name: "sellerOtherCostMeta",
  });
  const compensationArray = useFieldArray({
    control,
    name: "compensationMeta",
  });
  const otherCostArray = useFieldArray({ control, name: "otherCostMeta" });

  // route param and guards used by loadExisting and template loader
  const params = useParams();
  const calcId = params?.id || params?.calculatorId || params?.calcId;
  const loadedExistingRef = useRef(null);
  // Guard to avoid triggering heavy effects while we're programmatically populating RHF
  const isPopulatingRef = useRef(false);
  // Prevent concurrent loads for the same id
  const loadingExistingRef = useRef(false);

  // Load user template only AFTER companyId and form arrays are available
  // Provide handlers the template loader can use to populate RHF field arrays
  useUserTemplate(mainId, userId, companyId, {
    replaceSellerClosingCost: sellerClosingCostArray.replace,
    replaceClosingCostsSection2: closingCostsSection2Array.replace,
    replaceSellerOtherCost: sellerOtherCostArray.replace,
    replaceCompensation: compensationArray.replace,
    replaceOtherCost: otherCostArray.replace,
    // allow the template loader to set non-array RHF nodes directly when available
    setValue,
    // allow the hook to inspect current RHF values so it doesn't overwrite
    // addressMeta/loanInfoMeta when AddressSearchPreview already populated them
    getValues,
    // expose the current calcId and population guard so the hook can skip when editing/loading
    calcId,
    isPopulatingRef,
    // allow the hook to show a toast when template is missing
    showToaster: showToasterCallback,
  });

  // Watch RHF addressMeta.stateid and fetch state-specific settings when it becomes available
  const addressStateId =
    typeof watch === "function" ? watch("addressMeta.stateid") : undefined;

  // Watch loan type changes using RHF watch (don't watch salesPrice to avoid per-keystroke triggers)
  const watchedLoanType =
    typeof watch === "function"
      ? watch("loanInfoMeta.buyersLoanType")
      : undefined;
  const watchedHomeWarrantyAmount =
    typeof watch === "function"
      ? watch("loanInfoMeta.homeWarrantyAmount")
      : undefined;

  // watch stateSettings so effects depending on it run after it's populated
  const watchedStateSettings =
    typeof watch === "function" ? watch("stateSettings") : undefined;

  const watchedAnnualPropertyTax =
    typeof watch === "function"
      ? watch("loanInfoMeta.annualPropertyTaxAmount")
      : undefined;
  const watchedEstimatedClosingDate =
    typeof watch === "function"
      ? watch("loanInfoMeta.estimatedClosingDate")
      : undefined;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    if (netfirstDone) return;

    const onFocusOut = (e) => {
      try {
        // const values = getValues ? getValues() : {};
        //console.log('[SellerNetSheet] focusout ->', { name: e?.target?.name || e?.target?.id, values });
        //handleSellerCalculation();
      } catch (err) {
        // ignore logging errors
      }
    };

    //do the same for onchange of select inputs and blur of number inputs
    el.addEventListener("change", onFocusOut);
    el.addEventListener("blur", onFocusOut);

    return () => {
      el.removeEventListener("change", onFocusOut);
      el.removeEventListener("blur", onFocusOut);
    };
  }, [getValues, netfirstDone]);

  // Quick mode: default into manual editor (like Seller calculator) and seed a few defaults.
  useEffect(() => {
    if (!isQuickMode) return;
    try {
      // Show the manual sheet immediately.
      setShowManualEditor(true);
      setShowAddressView(false);

      // If we navigated into quick mode from an existing calc, preserve the address.
      try {
        let preserved = preservedAddressFromNav;
        if (!preserved) {
          try {
            const raw = sessionStorage.getItem(PRESERVED_ADDRESS_STORAGE_KEY);
            if (raw) preserved = JSON.parse(raw);
          } catch (e) {
            preserved = null;
          }
        }

        if (preserved && typeof preserved === "object" && Object.keys(preserved).length) {
          setValue && setValue("addressMeta", preserved);
          setIsCorrectProperty(true);
        }

        // One-shot: clear after applying so it doesn't leak into unrelated sessions.
        try {
          sessionStorage.removeItem(PRESERVED_ADDRESS_STORAGE_KEY);
        } catch (e) {
          /* ignore */
        }
      } catch (e) {
        // ignore
      }

      // Restore preserved loan info (quick-mode new calc) if present.
      try {
        const rawLoan = sessionStorage.getItem(PRESERVED_LOAN_INFO_STORAGE_KEY);
        if (rawLoan) {
          const preservedLoan = JSON.parse(rawLoan);
          if (preservedLoan && typeof preservedLoan === "object") {
            const currentLoan = (typeof getValues === "function"
              ? getValues("loanInfoMeta")
              : {}) || {};
            setValue && setValue("loanInfoMeta", { ...currentLoan, ...preservedLoan });
          }
        }
        sessionStorage.removeItem(PRESERVED_LOAN_INFO_STORAGE_KEY);
      } catch (e) {
        /* ignore */
      }

      // Restore annual property tax preference/value if present.
      try {
        const rawTax = sessionStorage.getItem(
          PRESERVED_ANNUAL_PROPERTY_TAX_STORAGE_KEY
        );
        if (rawTax) {
          const preservedTax = JSON.parse(rawTax) || {};
          if (typeof preservedTax.enabled !== "undefined") {
            setUseAnnualPropertyTax(!!preservedTax.enabled);
          }
          if (
            typeof preservedTax.amount !== "undefined" &&
            typeof setValue === "function"
          ) {
            setValue("loanInfoMeta.annualPropertyTaxAmount", preservedTax.amount);
            setValue("loanInfoMeta.annualPropertyTax", preservedTax.amount);
          }
        }
        sessionStorage.removeItem(PRESERVED_ANNUAL_PROPERTY_TAX_STORAGE_KEY);
      } catch (e) {
        /* ignore */
      }

      // Restore sellerOtherCostMeta (parsedSellerOtherCosts) if present.
      try {
        const rawSellerOther = sessionStorage.getItem(
          PRESERVED_SELLER_OTHER_COSTS_STORAGE_KEY
        );
        if (rawSellerOther) {
          const preservedSellerOther = JSON.parse(rawSellerOther);
          if (Array.isArray(preservedSellerOther) && preservedSellerOther.length) {
            const currentSellerOther =
              (typeof getValues === "function" ? getValues("sellerOtherCostMeta") : []) ||
              [];
            // Only apply if current is empty (avoid overwriting a freshly-loaded template).
            if (!Array.isArray(currentSellerOther) || currentSellerOther.length === 0) {
              if (sellerOtherCostArray && typeof sellerOtherCostArray.replace === "function") {
                sellerOtherCostArray.replace(preservedSellerOther);
              } else if (typeof setValue === "function") {
                setValue("sellerOtherCostMeta", preservedSellerOther);
              }
            }
          }
        }
        sessionStorage.removeItem(PRESERVED_SELLER_OTHER_COSTS_STORAGE_KEY);
      } catch (e) {
        /* ignore */
      }

      // Restore templateSellerOtherCosts (used for loan-type switching logic) if present.
      try {
        const rawTpl = sessionStorage.getItem(
          PRESERVED_TEMPLATE_SELLER_OTHER_COSTS_STORAGE_KEY
        );
        if (rawTpl) {
          const preservedTpl = JSON.parse(rawTpl);
          if (preservedTpl && typeof preservedTpl === "object") {
            const currentTpl =
              (typeof getValues === "function"
                ? getValues("templateSellerOtherCosts")
                : null) || null;
            const hasCurrent =
              currentTpl &&
              typeof currentTpl === "object" &&
              Object.keys(currentTpl).length;
            if (!hasCurrent && typeof setValue === "function") {
              setValue("templateSellerOtherCosts", preservedTpl);
            }
          }
        }
        sessionStorage.removeItem(PRESERVED_TEMPLATE_SELLER_OTHER_COSTS_STORAGE_KEY);
      } catch (e) {
        /* ignore */
      }

      // Basic defaults that reduce required clicks.
      const addDays = (date, days) => {
        const d = new Date(date);
        d.setDate(d.getDate() + days);
        return d;
      };
      const toDateStr = (d) =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
          d.getDate()
        ).padStart(2, "0")}`;
      const defaultEst = toDateStr(addDays(new Date(), 30));
      // Only set default if nothing is already present (preserved loanInfoMeta may include it).
      try {
        const existing =
          (typeof getValues === "function"
            ? getValues("loanInfoMeta.estimatedClosingDate")
            : undefined) || "";
        if (!existing) {
          setValue && setValue("loanInfoMeta.estimatedClosingDate", defaultEst);
        }
      } catch (e) {
        setValue && setValue("loanInfoMeta.estimatedClosingDate", defaultEst);
      }

      // Ensure fresh user intent for sellerMustNet (blank, not 0).
      setValue && setValue("sellerMustNet", 0);
    } catch (e) {
      // ignore
    }
  }, [isQuickMode, preservedAddressFromNav, setValue, getValues, sellerOtherCostArray]);

  useEffect(() => {
    if (!addressStateId) return; // wait until stateid is available in RHF
    if (netfirstDone) return;
    // Skip fetching state settings while we're programmatically populating the form
    try {
      if (typeof isPopulatingRef !== "undefined" && isPopulatingRef.current)
        return;
    } catch (e) {
      /* ignore */
    }

    const fetchStateSettings = async () => {
      try {
        const paramsStateSettings = {
          stateid: addressStateId,
          type: "seller_proration",
          maincompanyid: mainId,
        };
        const response = await getStateSettings(paramsStateSettings);

        if (response?.data) {
          // Persist into RHF so components can read stateSettings from the form if desired
          try {
            setValue && setValue("stateSettings", response.data);
          } catch (e) {
            console.warn("Failed to set stateSettings into RHF", e);
          }
        }
      } catch (error) {
        // ignore
      }
    };

    fetchStateSettings();
  }, [addressStateId, mainId, netfirstDone, setValue]);

  useEffect(() => {
    if (netfirstDone) return;
    try {
      if (templateMissing) {
        // centralize invalid address toast
        try {
          showInvalidAddressToast(handleUpdateToaster);
        } catch (e) {
          /* ignore */
        }
      }
    } catch (e) {
      /* ignore */
    }
  }, [netfirstDone, templateMissing]);

  useEffect(() => {
    if (netfirstDone) return;
    if (Number(ttbData?.taxAmount) > 0) {
      setUseAnnualPropertyTax(useAnnualPropertyTax);
    }
    if (Number(ttbData?.loanBalance?.balance) > 0) {
      setUseMortgageBalance(true);
    }
  }, [netfirstDone, ttbData, useAnnualPropertyTax]);

  // Keep RHF loan fields in sync with the mortgage balance preference (useMortgageBalance).
  // If mortgage balance is enabled, enable autoEstimateLoan and copy ttbData.loanBalance into loansToBePaid fields.
  // If disabled, disable autoEstimateLoan and copy mortgageAmount into loansToBePaidBalance1.
  useEffect(() => {
    if (netfirstDone) return;
    try {
      if (typeof setValue !== "function") return;

      // When programmatically populating the form, skip this sync to avoid cascading writes
      try {
        if (typeof isPopulatingRef !== "undefined" && isPopulatingRef.current)
          return;
      } catch (e) {
        /* ignore */
      }

      const enabled = !!useMortgageBalance;
      // set the RHF autoEstimateLoan flag to reflect parent state
      setValue("loanInfoMeta.autoEstimateLoan", enabled);

      if (enabled) {
        const bal = ttbData?.loanBalance?.balance ?? "";
        const rate = ttbData?.loanBalance?.interestRate ?? "";
        setValue("loanInfoMeta.loansToBePaidBalance1", bal);
        setValue("loanInfoMeta.loansToBePaidRate1", rate);
      } else {
        const mort =
          (typeof getValues === "function"
            ? getValues("loanInfoMeta.mortgageAmount")
            : "") || "";
        setValue("loanInfoMeta.loansToBePaidBalance1", mort || "0.00");
      }
    } catch (err) {
      // don't block render on sync errors
      console.warn("Failed syncing mortgage balance to RHF", err);
    }
    // only run when preference or ttbData loanBalance changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    useMortgageBalance,
    ttbData?.loanBalance?.balance,
    ttbData?.loanBalance?.interestRate,
    setValue,
    getValues,
    isPopulatingRef,
  ]);

  useEffect(() => {
    if (netfirstDone) return;
    // When user selects "No" for Home Warranty, write into RHF
    if (useHomeWarranty === false) {
      //update closingCostMeta from closingCostsSection2Array
      try {
        setValue("closingCostMeta", closingCostsSection2Array.fields);
      } catch (e) {
        console.warn("setValue failed for closingCostMeta", e);
      }
      try {
        setValue("loanInfoMeta.homeWarrantyAmount", "0.00");
      } catch (e) {
        console.warn("setValue failed for homeWarrantyAmount", e);
      }
    }
    // When user enables Home Warranty, try to find a matching closingCostMeta entry
    if (useHomeWarranty === true) {
      try {
        const closing = getValues ? getValues("closingCostMeta") || [] : [];
        if (Array.isArray(closing) && closing.length) {
          // find an item whose label or key looks like a home warranty entry
          const findIdx = (arr) => {
            for (let i = 0; i < arr.length; i++) {
              const it = arr[i] || {};
              const key = (it.key || "").toString().toLowerCase();
              const label = (it.label || "").toString().toLowerCase();
              // look for phrases like 'home warranty' or 'warranty' in label/key
              if (
                (label &&
                  label.includes("home") &&
                  (label.includes("warrant") || label.includes("warranty"))) ||
                label.includes("homewarranty") ||
                (key &&
                  key.includes("home") &&
                  (key.includes("warrant") || key.includes("warranty"))) ||
                key.includes("homewarranty")
              ) {
                return i;
              }
            }
            return -1;
          };

          const idx = findIdx(closing);
          const warrantyAmount =
            (getValues ? getValues("loanInfoMeta") || {} : {})
              ?.homeWarrantyAmount ?? "0.00";
          if (idx >= 0) {
            // update the found home warranty record with the amount and mark as flat fee
            try {
              setValue &&
                setValue(`closingCostMeta.${idx}.value`, warrantyAmount, {
                  shouldDirty: true,
                  shouldTouch: true,
                });
              setValue &&
                setValue(`closingCostMeta.${idx}.type`, "Flat Fee", {
                  shouldDirty: true,
                  shouldTouch: true,
                });
            } catch (e) {
              console.warn(
                "Failed setting closingCostMeta home warranty entry",
                e
              );
            }
          } else {
            // fallback: find an 'other' record and set it to 0 (and mark flat fee)
            const otherIdx = closing.findIndex(
              (it) =>
                (it && (it.key || "").toString().toLowerCase() === "other") ||
                ((it && it.camelKey) || "").toString().toLowerCase() === "other"
            );
            if (otherIdx >= 0) {
              try {
                setValue &&
                  setValue(`closingCostMeta.${otherIdx}.value`, "0.00", {
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                setValue &&
                  setValue(`closingCostMeta.${otherIdx}.type`, "Flat Fee", {
                    shouldDirty: true,
                    shouldTouch: true,
                  });
              } catch (e) {
                console.warn(
                  "Failed setting closingCostMeta other fallback for home warranty",
                  e
                );
              }
            }
          }
        }
      } catch (e) {
        console.warn("Failed handling useHomeWarranty true path", e);
      }
    }
    // include the homeWarranty amount value so this effect also runs when the user
    // formats/blurs the loanInfoMeta.homeWarrantyAmount field in PropertyDetailsSection
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useHomeWarranty, setValue, watchedHomeWarrantyAmount]);

  useEffect(() => {
    if (netfirstDone) return;
    if (!mainId) return;

    const fetchNationalSettings = async () => {
      try {
        const res = await getNationalSettings({ maincompanyid: mainId });
        const nationalData =
          Array.isArray(res?.data) && res.data.length > 0 ? res.data[0] : {};

        // Build a compact mapped object with only the keys our calculations need.
        try {
          const fha = nationalData?.fha_closing_costs || {};
          const va = nationalData?.va_closing_costs || {};
          const usda = nationalData?.usda_closing_costs || {};

          const FHA_PercentOne =
            fha?.mortgage_insurance?.years?.[0]?.percent ?? null;
          const conventionalLoan =
            nationalData?.conventional_loans?.conventional_loan_defaults
              ?.refinance_conventional_loan_ltv_percent ?? null;

          const ranges = Array.isArray(fha?.loan_amount_factor?.ranges)
            ? fha.loan_amount_factor.ranges
            : [];
          const findByLabel = (lbl) =>
            ranges.find(
              (r) =>
                String(r?.label || "").toLowerCase() ===
                String(lbl || "").toLowerCase()
            );
          const under = findByLabel("under");
          const to = findByLabel("to");
          const over = findByLabel("over");

          const mapped = {
            VA_FundingFee: va?.mortgage_insurance?.funding_fee_percent ?? null,
            USDA_MIPFactor:
              usda?.loan_amount_factor?.mip_factor_percent ?? null,
            FHA_PercentOne: FHA_PercentOne,
            FHA_SalePriceUnder: under?.sale_price ?? null,
            FHA_SalePriceUnderLTV: under?.ltv_percent ?? null,
            FHA_SalePriceTo: to?.sale_price ?? null,
            FHA_SalePriceToLTV: to?.ltv_percent ?? null,
            FHA_SalePriceOver: over?.sale_price ?? null,
            FHA_SalePriceOverLTV: over?.ltv_percent ?? null,
            conventionalLoan,
          };
          setNationalSettingsMap(mapped);
        } catch (err) {
          console.warn("Failed mapping national settings", err);
        }
      } catch (error) { }
    };

    fetchNationalSettings();
  }, [mainId, netfirstDone]);

  // If an `id` exists in the route params, load existing calculator data and populate RHF
  useEffect(() => {
    if (netfirstDone) return;
    const loadExisting = async () => {
      try {
        const id = calcId;
        if (!id) return;
        // don't reload the same record repeatedly
        if (loadedExistingRef.current === id) return;
        // avoid concurrent loads
        if (loadingExistingRef.current) return;
        loadingExistingRef.current = true;
        // mark that we're populating the form to allow other effects to skip work
        isPopulatingRef.current = true;
        // fetch existing record
        const resp = await getNetFirstCalc(id);
        if (!resp) return;
        const data = resp || {};

        // populate top-level fields into RHF
        try {
          if (typeof data.salesPrice !== "undefined")
            setValue("salesPrice", String(data.salesPrice || "0.00"));
          if (typeof data.preparedBy !== "undefined")
            setValue("preparedBy", data.preparedBy || "");
          if (typeof data.preparedFor !== "undefined")
            setValue("preparedFor", data.preparedFor || "");
          if (typeof data.addressMeta !== "undefined")
            setValue("addressMeta", data.addressMeta || {});
          if (typeof data.loanInfoMeta !== "undefined")
            setValue("loanInfoMeta", data.loanInfoMeta || {});
          if (typeof data.titleSettlementMeta !== "undefined")
            setValue("titleSettlementMeta", data.titleSettlementMeta || {});
          if (typeof data.totalClosingMeta !== "undefined")
            setValue("totalClosingMeta", data.totalClosingMeta || {});
        } catch (e) {
          console.warn(
            "Failed writing top-level RHF values for existing seller calc",
            e
          );
        }

        // replace field arrays when available
        try {
          if (Array.isArray(data.closingCostMeta)) {
            if (
              closingCostsSection2Array &&
              typeof closingCostsSection2Array.replace === "function"
            )
              closingCostsSection2Array.replace(data.closingCostMeta);
            else setValue && setValue("closingCostMeta", data.closingCostMeta);
          }

          if (Array.isArray(data.sellerClosingCostMeta)) {
            if (
              sellerClosingCostArray &&
              typeof sellerClosingCostArray.replace === "function"
            )
              sellerClosingCostArray.replace(data.sellerClosingCostMeta);
            else
              setValue &&
                setValue("sellerClosingCostMeta", data.sellerClosingCostMeta);
          }

          if (Array.isArray(data.sellerOtherCostMeta)) {
            if (
              sellerOtherCostArray &&
              typeof sellerOtherCostArray.replace === "function"
            )
              sellerOtherCostArray.replace(data.sellerOtherCostMeta);
            else
              setValue &&
                setValue("sellerOtherCostMeta", data.sellerOtherCostMeta);
          }

          if (Array.isArray(data.compensationMeta)) {
            if (
              compensationArray &&
              typeof compensationArray.replace === "function"
            )
              compensationArray.replace(data.compensationMeta);
            else
              setValue && setValue("compensationMeta", data.compensationMeta);
          }

          if (Array.isArray(data.otherCostMeta)) {
            if (otherCostArray && typeof otherCostArray.replace === "function")
              otherCostArray.replace(data.otherCostMeta);
            else setValue && setValue("otherCostMeta", data.otherCostMeta);
          }
        } catch (e) {
          console.warn(
            "Failed replacing RHF field arrays from existing seller calc",
            e
          );
        }

        // set company/main ids if present
        try {
          if (data.companyId) setCompanyId(data.companyId);
          if (data.mainCompanyId) setMainCompanyId(data.mainCompanyId);
        } catch (e) {
          /* ignore */
        }

        // Open manual editor and mark initial load (only if not already opened for this id)
        setShowManualEditor(true);
        setIsInitialLoad(true);
        // remember that we've loaded this id and clear loading state
        loadedExistingRef.current = id;
        loadingExistingRef.current = false;

        // After values loaded, recompute derived arrays and totals once.
        // Allow a short delay so RHF flushes the programmatic setValue calls.
        try {
          setTimeout(async () => {
            // allow other effects to resume
            isPopulatingRef.current = false;
            try {
              //await handleSalesPriceBlur();
            } catch (err) {
              /* ignore */
            }
            //try { handleSellerCalculation(); } catch (err) { /* ignore */ }
          }, 120);
        } catch (err) {
          /* ignore */
        }
      } catch (err) {
        console.warn("Failed loading existing seller calculator", err);
      }
    };

    loadExisting();
    // only run when the route id (calcId) changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calcId]);

  useEffect(() => {
    if (netfirstDone) return;
    if (
      initialSellerMustRef.current === null ||
      initialSellerMustRef.current === 0
    )
      return;
    // centralize handling for buyersLoanType changes: compute adjusted, update sellerOtherCostMeta, call title/escrow API, then recalc totals
    const handleLoanTypeChange = async () => {
      try {
        // Skip loan-type handling while programmatic population is in progress
        try {
          if (typeof isPopulatingRef !== "undefined" && isPopulatingRef.current)
            return;
        } catch (e) {
          /* ignore */
        }
        const sp = getValues ? getValues("salesPrice") : 0;
        //console.log("handleLoanTypeChange Sales Price ", sp);

        const spNum = Number(String(sp || "").replace(/[^0-9.-]+/g, "")) || 0;
        const lt =
          watchedLoanType ||
          (getValues
            ? getValues("loanInfoMeta.buyersLoanType")
            : "Conventional");

        if (spNum <= 0) return;

        // 1) compute adjusted amounts for the given loan type
        try {
          await computeAdjustedForLoanType(spNum, lt);
        } catch (err) {
          console.warn(
            "computeAdjustedForLoanType failed during loanType change",
            err
          );
        }

        // 2) update sellerOtherCostMeta from templateSellerOtherCosts according to loan type
        try {
          const templates =
            (getValues && getValues("templateSellerOtherCosts")) || {};
          let newArr = [];
          const loan = String(lt || "").trim();
          if (loan === "FHA 203b") newArr = templates.fha || [];
          else if (loan === "VA") newArr = templates.va || [];
          else if (loan === "USDA") newArr = templates.usda || [];
          //else if (loan === 'Conventional') newArr = templates.conventional || [];
          else if (
            loan === "Conventional" ||
            loan === "Cash" ||
            loan === "Owner Carry"
          ) {
            const base = templates.fha || [];
            newArr = base.map((it) => ({ ...it, value: "0.00" }));
          }

          // if percent units exist compute amounts from salesPrice
          const salesPrice = spNum;
          // apply any manual overrides set by the user so those values persist across loan type changes
          const overrides =
            (getValues && getValues("sellerOtherCostMetaOverrides")) || {};
          newArr.forEach((item) => {
            const camel = item.camelKey || item.key || "";
            if (overrides && camel && typeof overrides[camel] !== "undefined") {
              // honor user-entered override (string formatted) and skip template-derived calc
              item.value = overrides[camel];
            } else if (item.unit === "%") {
              const value = Number(item.value || 0);
              item.amount = (salesPrice * value) / 100;
            }
          });

          // Ensure EXTRA_KEYS exist and IGNORE_KEYS removed
          const EXTRA_KEYS = new Set(["corrective_work", "buyers_fees"]);
          const IGNORE_KEYS = new Set(["points_pct", "origination_factor_pct"]);
          newArr = Array.isArray(newArr)
            ? newArr.filter((it) => !IGNORE_KEYS.has((it.key || "").toString()))
            : [];
          for (const k of EXTRA_KEYS) {
            if (!newArr.find((x) => (x.key || "").toString() === k)) {
              const camel =
                k === "corrective_work" ? "correctiveWork" : "buyersFees";
              const defaultShape = {
                key: k,
                camelKey: camel,
                unit: "$",
                label:
                  k === "corrective_work" ? "Corrective Work" : "Buyer's Fees",
                sr_no: k === "corrective_work" ? 7 : 8,
                value: "0.00",
              };
              newArr.push(defaultShape);
            }
          }

          if (
            sellerOtherCostArray &&
            typeof sellerOtherCostArray.replace === "function"
          ) {
            sellerOtherCostArray.replace(newArr || []);
          } else {
            setValue && setValue("sellerOtherCostMeta", newArr || []);
          }
        } catch (e) {
          console.warn(
            "Failed to update sellerOtherCostMeta for loan type change",
            e
          );
        }

        // 3) call title/escrow API with updated form values
        try {
          await getFACCData(getValues ? getValues() : {});
        } catch (e) {
          console.warn("getFACCData failed during loan type change", e);
        }

        // Recompute per-row totals for closingCostMeta (same logic as ManualField.useEffect)
        try {
          const closing = Array.isArray(
            getValues ? getValues("closingCostMeta") : []
          )
            ? getValues
              ? getValues("closingCostMeta")
              : []
            : [];
          const salesPriceRaw =
            (getValues ? getValues("salesPrice") : "") || "";
          const salesPrice =
            Number(String(salesPriceRaw).replace(/[^0-9.-]+/g, "")) || 0;
          const loanAdjRaw = (getValues ? getValues("loanInfoMeta") || {} : {})
            ?.adjusted;
          const loanAmount =
            Number(String(loanAdjRaw || "").replace(/[^0-9.-]+/g, "")) ||
            salesPrice;

          if (Array.isArray(closing) && closing.length) {
            for (let i = 0; i < closing.length; i++) {
              try {
                const item = closing[i] || {};
                const typeVal = (item.type || "").toString();
                const rawVal = (item.value ?? item.rate ?? "").toString();
                const cleaned = String(rawVal ?? "").replace(/[^0-9.-]+/g, "");
                const num = cleaned === "" ? NaN : Number(cleaned);
                let newTotal = NaN;
                if (!Number.isFinite(num)) {
                  newTotal = NaN;
                } else if (/%\s*sale|sale price|sale/i.test(typeVal)) {
                  newTotal = salesPrice * (num / 100) || 0;
                } else if (/%\s*loan|loan/i.test(typeVal)) {
                  newTotal = loanAmount * (num / 100) || 0;
                } else {
                  newTotal = num;
                }

                if (Number.isFinite(newTotal)) {
                  const currTotal =
                    (getValues
                      ? getValues(`closingCostMeta.${i}.total`)
                      : undefined) || 0;
                  if (Number(currTotal) !== Number(newTotal)) {
                    try {
                      setValue &&
                        setValue(`closingCostMeta.${i}.total`, newTotal, {
                          shouldDirty: true,
                          shouldTouch: true,
                        });
                    } catch (e) {
                      // ignore per-row set failures
                    }
                  }
                }
              } catch (e) {
                // ignore per-item errors
              }
            }
          }
        } catch (e) {
          console.warn(
            "Failed recomputing closingCostMeta totals during loan type change",
            e
          );
        }

        // 4) finalize with recalculation of totals
        try {
          //console.log("Calling handleSellerCalculation after loanType change");
          handleSellerCalculation();
        } catch (e) {
          console.warn(
            "handleSellerCalculation failed after loanType change",
            e
          );
        }
      } catch (err) {
        console.warn("watchedLoanType handler error", err);
      }
    };

    handleLoanTypeChange();
  }, [
    watchedLoanType,
    nationalSettingsMap,
    getValues("loanInfoMeta.conventionalPercent"),
  ]);

  // Calculate estimated Tax Prorations
  useEffect(() => {
    if (netfirstDone) return;
    // Basic prechecks
    const formData = getValues ? getValues() : {};
    // prefer the watched stateSettings so this effect runs after fetchStateSettings writes into RHF
    const data1Proration =
      watchedStateSettings || formData?.stateSettings || {};
    //console.log("useEffect for estimated tax prorations triggered:", { taxAmount: ttbData?.taxAmount, data1Proration });
    const taxAmount = Number(ttbData?.taxAmount || 0);

    if (taxAmount <= 0 || !Object.keys(data1Proration).length) return;

    setUseAnnualPropertyTax(useAnnualPropertyTax);

    //Calculate safely
    const estimatedClosingDate = formData?.loanInfoMeta?.estimatedClosingDate;

    const annualPropertyTax =
      Number(
        String(formData?.loanInfoMeta?.annualPropertyTaxAmount).replace(
          /[^0-9.-]+/g,
          ""
        )
      ) || 0;
    //console.log("Calculating estimated tax prorations with:", { estimatedClosingDate, annualPropertyTax, data1Proration });
    const estimatedTax = getSellerEstimatedTax(
      estimatedClosingDate,
      annualPropertyTax,
      data1Proration,
      "seller"
    );

    if (!estimatedTax || isNaN(estimatedTax.estimatedTaxProrations)) return;
    //update estimatedTax.estimatedTaxProrations to loanInfoMeta.estimatedTaxProrations in RHF
    try {
      const prev = getValues ? getValues() : {};
      setValue("loanInfoMeta", {
        ...(prev?.loanInfoMeta || {}),
        estimatedTaxProrations: estimatedTax.estimatedTaxProrations,
      });
    } catch (e) {
      console.warn(
        "setValue failed updating loanInfoMeta estimatedTaxProrations",
        e,
        estimatedTax.estimatedTaxProrations
      );
    }
  }, [
    ttbData?.taxAmount,
    watchedStateSettings,
    getValues,
    setValue,
    watchedAnnualPropertyTax,
    watchedEstimatedClosingDate,
  ]);

  useEffect(() => {
    if (netfirstDone) return;
    try {
      const current = getValues ? getValues() : {};
      const existingLoanInfo = current?.loanInfoMeta || {};

      // Decide what value to set dynamically
      const newAnnualPropertyTax = useAnnualPropertyTax
        ? existingLoanInfo?.annualPropertyTax ?? 0
        : 0;

      setValue("loanInfoMeta", {
        ...existingLoanInfo,
        annualPropertyTaxAmount: newAnnualPropertyTax,
      });
    } catch (e) {
      console.log("Error in useEffect annualPropertyTax:", e);
    }
  }, [getValues, netfirstDone, setValue, useAnnualPropertyTax]);

  // When salesPrice is formatted/blurred in the PropertyDetailsSection, update
  // compensation, otherCost and closingCost entries derived from the sales price.
  const handleSalesPriceBlur = async (rawValue) => {
    try {
      // If we're currently populating the form from an existing calc, skip heavy work
      try {
        if (typeof isPopulatingRef !== "undefined" && isPopulatingRef.current)
          return;
      } catch (e) {
        // ignore
      }

      // Step 1: compute adjusted/amount for the current loan type (only if valid sales price)
      try {
        const current = getValues ? getValues() : {};
        let sellerMustNet = parseCurrency(current?.sellerMustNet);
        //console.log("initialSellerMust 11 - ", initialSellerMustRef.current, getValues('sellerMustNet'));
        if (!initialSellerMustRef.current) {
          setInitialSellerMustAndRef(sellerMustNet);
        }
        //console.log("initialSellerMust 11 - ", initialSellerMustRef.current, getValues('sellerMustNet'));

        let loanInfoMetaTotal =
          parseCurrency(current?.loanInfoMeta?.loansToBePaidBalance1) +
          parseCurrency(current?.loanInfoMeta?.loansToBePaidBalance2) +
          parseCurrency(current?.loanInfoMeta?.loansToBePaidBalance3);
        let sellerWantTotal =
          parseCurrency(sellerMustNet) + parseCurrency(loanInfoMetaTotal);
        //console.log("sellerMustNet ", sellerMustNet);
        //console.log("loanInfoMetaTotal ", loanInfoMetaTotal);
        //console.log("sellerWantTotal ", sellerWantTotal);
        let salesPrice = parseCurrency(sellerWantTotal * 1.08).toFixed(2);

        setValue("salesPrice", salesPrice);
        //console.log("handleSalesPriceBlur Sales Price ", salesPrice);

        // const spNum = Number(String(sp || '').replace(/[^0-9.-]+/g, '')) || 0;
        const lt =
          watchedLoanType ||
          (getValues
            ? getValues("loanInfoMeta.buyersLoanType")
            : "Conventional");
        if (salesPrice > 0) {
          // await to ensure compute finishes before persisting arrays
          try {
            await computeAdjustedForLoanType(salesPrice, lt);
          } catch (err) {
            console.warn(
              "computeAdjustedForLoanType failed during salesPrice blur",
              err
            );
          }
        }
      } catch (e) {
        console.warn(
          "handleSalesPriceBlur: failed computing adjusted value",
          e
        );
      }
      const current = getValues ? getValues() : {};

      // 2) call title/escrow API with updated form values
      try {
        await getFACCData(current);
      } catch (e) {
        console.warn("getFACCData failed during loan type change", e);
      }

      // Step 3: derive updated arrays based on sales price
      const updated = updateDerivedFields(current || {});

      // Step 4: Persist arrays via useFieldArray.replace when available (preferred)
      try {
        if (Array.isArray(updated.compensationMeta)) {
          if (
            compensationArray &&
            typeof compensationArray.replace === "function"
          )
            compensationArray.replace(updated.compensationMeta);
          else
            setValue && setValue("compensationMeta", updated.compensationMeta);
        }

        if (Array.isArray(updated.otherCostMeta)) {
          if (otherCostArray && typeof otherCostArray.replace === "function")
            otherCostArray.replace(updated.otherCostMeta);
          else setValue && setValue("otherCostMeta", updated.otherCostMeta);
        }

        if (Array.isArray(updated.closingCostMeta)) {
          if (
            closingCostsSection2Array &&
            typeof closingCostsSection2Array.replace === "function"
          )
            closingCostsSection2Array.replace(updated.closingCostMeta);
          else setValue && setValue("closingCostMeta", updated.closingCostMeta);
        }
        if (Array.isArray(updated.sellerClosingCostMeta)) {
          if (
            sellerClosingCostArray &&
            typeof sellerClosingCostArray.replace === "function"
          )
            sellerClosingCostArray.replace(updated.sellerClosingCostMeta);
          else
            setValue &&
              setValue("sellerClosingCostMeta", updated.sellerClosingCostMeta);
        }
      } catch (err) {
        console.warn(
          "handleSalesPriceBlur: failed persisting derived arrays",
          err
        );
      }

      // Step 5: compute totals and other dependent calculations
      try {
        //console.log("Calling handleSellerCalculation after salesPrice blur");
        handleSellerCalculation();
      } catch (err) {
        console.warn("handleSalesPriceBlur: failed", err);
      }
    } catch (e) {
      console.warn("handleSalesPriceBlur: failed to update derived fields", e);
    }
  };

  // Compute adjusted amount and amount when salesPrice or loanType or national settings change
  const computeAdjustedForLoanType = async (salePriceVal, loanTypeVal) => {
    //console.log('computeAdjustedForLoanType triggered:', { salePriceVal, loanTypeVal, nationalSettingsMap });
    try {
      // If we're populating the form, skip computing adjusted to avoid cascading writes
      try {
        if (typeof isPopulatingRef !== "undefined" && isPopulatingRef.current)
          return;
      } catch (e) {
        /* ignore */
      }
      const salesPriceNum =
        Number(String(salePriceVal || "").replace(/[^0-9.-]+/g, "")) || 0;
      if (!salesPriceNum || salesPriceNum <= 0) return;

      const loanType = (
        loanTypeVal ||
        (getValues && getValues("loanInfoMeta.buyersLoanType")) ||
        (getValues && getValues("loanInfoMeta")?.buyersLoanType) ||
        ""
      ).toString();

      // Build a request object for calculateAdjusted
      const req = { salePrice: salesPriceNum, loanType };

      // VA
      if (String(loanType).toLowerCase().indexOf("va") !== -1) {
        req.LTV = nationalSettingsMap?.VA_FundingFee ?? 0;
      }

      // USDA
      if (String(loanType).toLowerCase().indexOf("usda") !== -1) {
        req.MIP = nationalSettingsMap?.USDA_MIPFactor ?? 0;
      }

      // Conventional: prefer form conventionalPercent for LTV and form MIP if present
      if (String(loanType).toLowerCase().indexOf("conventional") !== -1) {
        req.LTV =
          Number(
            getValues ? getValues("loanInfoMeta.conventionalPercent") : 0
          ) || 0;
        req.MIP = Number(getValues ? getValues("loanInfoMeta.MIP") : 0) || 0;
      }

      // FHA: include thresholds and percent from nationalSettingsMap
      if (String(loanType).toLowerCase().indexOf("fha") !== -1) {
        req.MIP = nationalSettingsMap?.FHA_PercentOne ?? 0;
        req.FHA = {
          FHA_SalePriceUnder: nationalSettingsMap?.FHA_SalePriceUnder ?? 0,
          FHA_SalePriceUnderLTV:
            nationalSettingsMap?.FHA_SalePriceUnderLTV ?? 0,
          FHA_SalePriceTo: nationalSettingsMap?.FHA_SalePriceTo ?? 0,
          FHA_SalePriceToLTV: nationalSettingsMap?.FHA_SalePriceToLTV ?? 0,
          FHA_SalePriceOver: nationalSettingsMap?.FHA_SalePriceOver ?? 0,
          FHA_SalePriceOverLTV: nationalSettingsMap?.FHA_SalePriceOverLTV ?? 0,
        };
      }

      if (String(loanType).toLowerCase().indexOf("owner carry") !== -1) {
        req.allowOwnerCarry1Check = Number(
          getValues ? getValues("loanInfoMeta.allowOwnerCarry1Check") : false
        );
        req.allowOwnerCarry2Check = Number(
          getValues ? getValues("loanInfoMeta.allowOwnerCarry2Check") : false
        );
        req.amount = Number(getValues ? getValues("loanInfoMeta.amount") : 0);
        req.amount2 = Number(getValues ? getValues("loanInfoMeta.amount2") : 0);
      }

      const res = calculateAdjusted(req);
      //console.log('computeAdjustedForLoanType result:', res);
      // write into RHF loanInfoMeta.amount and loanInfoMeta.adjusted
      try {
        const prev = getValues ? getValues() : {};
        const prevLoan = (prev && prev.loanInfoMeta) || {};
        setValue("loanInfoMeta", {
          ...(prevLoan || {}),
          amount: res.amount ?? 0,
          adjusted: res.adjusted ?? 0,
        });
      } catch (e) {
        console.warn(
          "computeAdjustedForLoanType: failed to set loanInfoMeta",
          e
        );
      }
    } catch (err) {
      console.warn("computeAdjustedForLoanType error", err);
    }
  };

  const getFACCData = async (updatedForm) => {
    try {
      // Skip if form is being populated programmatically
      if (isPopulatingRef?.current) return;
    } catch (e) {
      /* ignore */
    }

    const salePriceRaw = updatedForm.salesPrice;
    const adjustedRaw = updatedForm?.loanInfoMeta?.adjusted;

    const salePrice =
      Number(String(salePriceRaw || 0).replace(/[^0-9.-]+/g, "")) || 0;
    const adjusted =
      Number(String(adjustedRaw || 0).replace(/[^0-9.-]+/g, "")) || 0;

    //console.log("getFACCData() salePrice adjusted - request:", { salePrice, adjusted });
    // -----------------------------------
    // NEW: Skip API call if nothing changed (normalize numbers)
    // -----------------------------------
    const last = lastFACCRequestRef.current || {};

    if (
      salePrice === last.salePrice &&
      adjusted === last.adjusted &&
      last.data // ensure we have cached data
    ) {
      // Restore previous computed meta to UI
      try {
        setValue("titleSettlementMeta", last.data);
      } catch (err) {
        console.warn("Failed restoring cached titleSettlementMeta:", err);
      }
      return; // STOP HERE — SKIP API
    }

    // Build payload
    const payload = {
      closing_state: updatedForm.state,
      closing_city: updatedForm.city,
      closing_county: updatedForm.county,
      county: updatedForm.county,
      city: updatedForm.city,
      zip:
        (updatedForm &&
          updatedForm.addressMeta &&
          updatedForm.addressMeta.zip) ||
        updatedForm.zip ||
        "",
      salePrice,
      adjusted,
      loanType:
        updatedForm.loanInfoMeta?.buyersLoanType || updatedForm.buyersLoanType,
      estStlmtDate: updatedForm.loanInfoMeta?.estimatedClosingDate,
      userId,
      device: "web",
      calc: "seller",
      reissueyr: false,
      mrs_id: "Gf40rntTRu",
      prepared_for: updatedForm.preparedFor,
      liab_amount: "0.00",
      sim: true,
    };

    const params = { mainid: mainId, userid: userId, companyid: companyId };

    // -----------------------------------
    // SAFE API CALL
    // -----------------------------------
    let dataTitleEscrow = null;
    try {
      dataTitleEscrow = await getTitleEscrowCosts(payload, params);
    } catch (err) {
      console.warn("getTitleEscrowCosts failed:", err);
      return;
    }

    const d = dataTitleEscrow?.data || {};

    // Cache normalized request & response to avoid duplicate API calls
    try {
      lastFACCRequestRef.current = { salePrice, adjusted, data: d };
    } catch (e) {
      // ignore
    }

    // -----------------------------------
    // ESCROW SETTLEMENT CALC
    // -----------------------------------
    let escrowSettlementAmount = "0.00";
    if (d.escrowType === "Split") {
      escrowSettlementAmount = d.escrowFeeBuyer ?? "0.00";
    } else if (d.escrowType === "Seller") {
      escrowSettlementAmount = d.escrowFee ?? "0.00";
    }

    // -----------------------------------
    // OWNER TITLE
    // -----------------------------------
    let ownersTitleAmount = "0.00";
    if (d.ownerType === "Split") {
      ownersTitleAmount = (parseCurrency(d.ownerFee) / 2).toString() ?? "0.00";
    } else if (d.ownerType === "Seller") {
      ownersTitleAmount = d.ownerFee ?? "0.00";
    }

    // -----------------------------------
    // LENDER TITLE
    // -----------------------------------
    let lendersTitleAmount = "0.00";
    if (d.lenderType === "Split") {
      lendersTitleAmount =
        (parseCurrency(d.lenderFee) / 2).toString() ?? "0.00";
    } else if (d.lenderType === "Seller") {
      lendersTitleAmount = d.lenderFee ?? "0.00";
    }

    // -----------------------------------
    // FINAL DERIVED META
    // -----------------------------------
    const titleSettlementMeta = {
      ownersTitleAmount,
      ownersTitleSeller: d.ownerFee ?? "0.00",
      ownersTitlePayor: d.ownerType ?? "Seller",

      escrowSettlementPayor: d.escrowType ?? "Seller",
      escrowSettlementAmount,
      escrowSettlementSeller: d.escrowFee ?? "0.00",
      escrowSettlementSplit: d.escrowFeeSeller ?? "0.00",

      lendersTitleAmount,
      lendersTitleSeller: d.lenderFee ?? "0.00",
      lendersTitlePayor: d.lenderType ?? "Seller",
    };

    // -----------------------------------
    // UPDATE UI
    // -----------------------------------
    try {
      setValue("titleSettlementMeta", titleSettlementMeta);
      //console.log("tleSettlementMeta", titleSettlementMeta);
    } catch (e) {
      console.warn("setValue failed updating titleSettlementMeta", e);
    }

    // Store computed result for next comparison
    lastFACCRequestRef.current.data = titleSettlementMeta;
  };

  const handleCancelModal = () => {
    closeModal();
    setTimeout(() => {
      openModal({
        title: "Seller2Net Sheet Results",
        size: "halffull",
        content: renderModal(1),
      });
    }, 50);
  };

  const handleCancelResultModal = () => {
    closeModal();
    setTimeout(() => {
      openModal({
        title: "What would you like to do next?",
        size: "smallmodel",
        content: renderModal(2),
      });
    }, 50);
  };
  const handleSellerCalculation = async () => {
    if (inCalcRef.current) return;
    inCalcRef.current = true;
    try {
      const currentInit = initialSellerMustRef.current;
      //console.log("handleSellerCalculation initialSellerMust", currentInit);
      if (currentInit === null || currentInit === 0) {
        // Nothing to iterate.
        resolveAwaitNetFirstCompletion({ status: "skipped" });
        return;
      }
      const totalsAgg = computeAllTotals(getValues ? getValues() : {});

      const totalsNode = {
        titleSettlementMetaTotal: totalsAgg.titleSettlementMetaTotal,
        sellerClosingCostMetaTotal: totalsAgg.sellerClosingCostMetaTotal,
        compensationMetaTotal: totalsAgg.compensationMetaTotal,
        closingCostMetaTotal: totalsAgg.closingCostMetaTotal,
        sellerOtherCostMetaTotal: totalsAgg.sellerOtherCostMetaTotal,
        sellerCostsTotal: totalsAgg.sellerCostsTotal,
        otherCostsTotal: totalsAgg.otherCostsTotal,
        allCostsTotal: totalsAgg.allCostsTotal,
        sellerMustNet: totalsAgg.sellerMustNet,
      };
      //console.log("sellerMustNet ", totalsNode.sellerMustNet);
      try {
        setValue("totalClosingMeta", totalsNode);
        // Increment counter and call calSellerWant with the new value.
        // Use functional updater and invoke calSellerWant from inside to ensure
        // we pass the updated value and avoid stale-state re-entrancy which
        // caused an infinite loop previously.
        setCounter((prev) => {
          const next = Math.min(prev + 1, 10);
          // schedule the heavy recalculation on next tick to break synchronous re-entry
          setTimeout(() => {
            //console.log("Counter Timeout ", next);
            try {
              calSellerWant(next);
            } catch (err) {
              console.warn("calSellerWant failed", err);
            }
          }, 20);
          //console.log("Counter ", next);
          return next;
        });
      } catch (e) {
        console.warn(
          "setValue failed updating totalClosingMeta",
          e,
          totalsNode
        );
      }
    } catch (e) {
      // ignore
    } finally {
      inCalcRef.current = false;
    }
  };

  const calSellerWant = async (counter) => {
    if (inCalcRef.current) return;
    inCalcRef.current = true;
    try {
      setNetfirstDone(true);
      let maintain_amount = Number(maintainAmountRef.current) || 0;
      let balance = 0;
      let netDiff = 0;
      let sellerMustNet = 0;
      let sellerWantTotal = 0;
      let salesPrice = 0;
      const current = getValues ? getValues() : {};

      //console.log("initialSellerMust 22 - ", initialSellerMustRef.current);
      if (!initialSellerMustRef.current) {
        //console.log("calSellerWant 2 ", initialSellerMustRef.current);
        setInitialSellerMustAndRef(current.sellerMustNet);
      }
      // ensure we reference the latest value (may have been set above)
      const currentInitial = initialSellerMustRef.current;
      const currentInitialNum = parseCurrency(currentInitial);
      const totalLoansToBePaidBalance =
        parseCurrency(current.loanInfoMeta?.loansToBePaidBalance1) +
        parseCurrency(current.loanInfoMeta?.loansToBePaidBalance2) +
        parseCurrency(current.loanInfoMeta?.loansToBePaidBalance3);

      // return true;
      balance =
        parseCurrency(current.salesPrice) -
        parseCurrency(current.totalClosingMeta?.allCostsTotal);
      balance = balance - parseCurrency(totalLoansToBePaidBalance);
      balance =
        balance - parseCurrency(current.loanInfoMeta?.estimatedTaxProrations);

      netDiff = Number(balance) - Number(currentInitialNum);
      sellerMustNet = Number(balance).toFixed(2);

      //console.log('salesPrice', current.salesPrice,'allCostsTotal', current.totalClosingMeta?.sellerCostsTotal, 'totalLoansToBePaidBalance', totalLoansToBePaidBalance, 'estimatedTaxProrations', current.loanInfoMeta?.estimatedTaxProrations);
      //console.log('balance', balance, 'netDiff', netDiff, 'currentInitialNum', currentInitialNum);

      try {
        if (typeof setValue === "function")
          setValue("sellerMustNet", sellerMustNet);
      } catch (e) {
        console.warn("Failed setting sellerMustNet into RHF", e);
      }

      if (counter >= 10) {
        resolveAwaitNetFirstCompletion({ status: "max-iterations", counter });
        return true; // stop the recursive call
      }

      if (netDiff > 1000) {
        //console.log("netDiff > 1000 && netDiff > 0");

        maintain_amount = (Number(maintain_amount) || 0) - 950;
        setMaintainAmountAndRef(maintain_amount);

        sellerWantTotal =
          currentInitialNum +
          totalLoansToBePaidBalance +
          parseCurrency(maintain_amount);

        salesPrice = (parseCurrency(sellerWantTotal) * 1.08).toFixed(2);
        setValue("salesPrice", salesPrice);
        await calculateClosingCosts(salesPrice);
      } else {
        //console.log("netDiff < 0 (shortfall)");

        if (balance < currentInitialNum) {
          netDiff = currentInitialNum - balance;

          if (netDiff > 200) {
            //console.log("netDiff > 200");
            maintain_amount = (Number(maintain_amount) || 0) + 500;
            setMaintainAmountAndRef(maintain_amount);
            sellerWantTotal =
              currentInitialNum +
              parseCurrency(totalLoansToBePaidBalance) +
              parseCurrency(maintain_amount);

            salesPrice = (parseCurrency(sellerWantTotal) * 1.08).toFixed(2);
            setValue("salesPrice", salesPrice);
            await calculateClosingCosts(salesPrice);
          } else {
            //console.log("netDiff <= 500");
            maintain_amount = (Number(maintain_amount) || 0) + 100;
            setMaintainAmountAndRef(maintain_amount);
            sellerWantTotal =
              currentInitialNum +
              parseCurrency(totalLoansToBePaidBalance) +
              parseCurrency(maintain_amount);

            salesPrice = (parseCurrency(sellerWantTotal) * 1.08).toFixed(2);
            setValue("salesPrice", salesPrice);
            await calculateClosingCosts(salesPrice);
          }
        } else {
          console.log("netDiff within tolerance", getValues());
          //console.log("netDiff within tolerance", netDiff);
          //console.log("calSellerWant - current ", current);
          resolveAwaitNetFirstCompletion({
            status: "converged",
            counter,
            netDiff,
          });
          return;
        }
        //console.log("calSellerWant Recursion - salesPrice ", salesPrice);
      }
    } catch (e) {
      // If an unexpected error breaks the iteration chain, unblock the UI.
      try {
        resolveAwaitNetFirstCompletion({ status: "error", error: e });
      } catch (err) {
        /* ignore */
      }
    } finally {
      inCalcRef.current = false;
    }
  };

  const calculateClosingCosts = async (salesPrice) => {
    //console.log("calculateClosingCosts called salesPrice ", salesPrice);
    try {
      const lt =
        watchedLoanType ||
        (typeof getValues === "function"
          ? getValues("loanInfoMeta.buyersLoanType")
          : "Conventional");

      // Step 1 — compute adjusted loan values
      try {
        await computeAdjustedForLoanType(salesPrice, lt);
      } catch (err) {
        console.warn("computeAdjustedForLoanType failed:", err);
      }

      // Step 2 — read current form state
      let current = {};
      try {
        current = typeof getValues === "function" ? getValues() : {};
      } catch (err) {
        console.warn("getValues() failed:", err);
        current = {};
      }

      // Step 3 — fetch FACC data
      try {
        await getFACCData(current);
      } catch (err) {
        console.warn("getFACCData failed:", err);
      }

      // Step 4 — update derived fields
      let updated = {};
      try {
        updated = updateDerivedFields(current || {});
      } catch (err) {
        console.warn("updateDerivedFields failed:", err);
        updated = {};
      }

      // Step 5 — safely update form arrays
      try {
        // Compensation Meta
        if (Array.isArray(updated.compensationMeta)) {
          if (compensationArray?.replace) {
            compensationArray.replace(updated.compensationMeta);
          } else if (typeof setValue === "function") {
            setValue("compensationMeta", updated.compensationMeta);
          }
        }

        // Other Cost Meta
        if (Array.isArray(updated.otherCostMeta)) {
          if (otherCostArray?.replace) {
            otherCostArray.replace(updated.otherCostMeta);
          } else if (typeof setValue === "function") {
            setValue("otherCostMeta", updated.otherCostMeta);
          }
        }

        // Closing Cost Meta
        if (Array.isArray(updated.closingCostMeta)) {
          if (closingCostsSection2Array?.replace) {
            closingCostsSection2Array.replace(updated.closingCostMeta);
          } else if (typeof setValue === "function") {
            setValue("closingCostMeta", updated.closingCostMeta);
          }
        }
      } catch (err) {
        console.warn("persisting derived arrays failed:", err);
      }

      // Step 6 — final seller calculation
      // IMPORTANT: schedule this after the current stack so it doesn't get
      // blocked by `inCalcRef` while `calSellerWant` is still running.
      try {
        setTimeout(() => {
          try {
            handleSellerCalculation();
          } catch (err) {
            console.warn("handleSellerCalculation1 failed:", err);
          }
        }, 0);
      } catch (err) {
        console.warn("handleSellerCalculation1 failed:", err);
      }
    } catch (err) {
      // final catch-all
      console.error("Fatal error in sales price recalculation flow:", err);
    }
  };

  const handleDownloadPrintPDF = async (type) => {
    try {
      const isValid = checkValidSalesPrice();
      const user = JSON.parse(localStorage.getItem("user"));
      if (!isValid) return;
      const payload = buildSellerNetPayload({
        getVal,
        user,
        calculatorname: "Seller2Net Sheet",
        mrsid: getValues("addressMeta")?.mrsid,
        optionType: type === "download" ? "download" :
          type === "print" ? "printdetailedestimate" :
            "download"
      });
      //  const printDisclaimer = await fetchPrintDisclaimerForPdf({ maincompanyid: mainId, calcSlug: undefined, pdfType: "seller2Net", });
      // console.log('Generating PDF with payload:', payload);
      const data = await generatePdf({
        type: "seller2Net", data: {
          ...payload,
          //   disclaimer: printDisclaimer 
        }
      });
      // console.log('Received PDF data from server:', data);

      const blob = new Blob([data], { type: "application/pdf" });
      const blobUrl = window.URL.createObjectURL(blob);

      if (type === "download") {
        const link = document.createElement("a");
        link.href = blobUrl;
        //   link.download = `document-seller-${new Date()}.pdf`;

        let preparedFor = (payload?.preparedfor || "New_Client")
          .replace(/\s+/g, "_") // replace spaces with _
          .replace(/[^a-zA-Z0-9_]/g, ""); // remove unsafe characters

        let timestamp = Math.floor(Date.now() / 1000); // 10-digit timestamp

        link.download = `Print_for_${preparedFor}_${timestamp}.pdf`;

        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
      }

      if (type === "print") {
        const printWindow = window.open("", "_blank", "width=900,height=700");

        if (printWindow) {
          printWindow.document.write(`
      <html>
        <head>
          <title>Print PDF</title>
        </head>
        <body style="margin:0;">

          <iframe id="printFrame"
            src="${blobUrl}"
            style="width:100%; height:100%; border:none;">
          </iframe>

          <script>
            const frame = document.getElementById('printFrame');
            frame.onload = function () {
              // wait a bit for PDF viewer to fully initialize
              setTimeout(() => {
                frame.contentWindow.focus();
                frame.contentWindow.print();
              }, 300);
            };
          </script>

        </body>
      </html>
    `);

          printWindow.document.close();
        }
      }
    } catch (error) {
      console.warn("Error while downloading and print pdf", error);
    }
  };
  const checkValidSalesPrice = () => {
    const formData = getValues();
    //console.log("formData", formData);
    const formatted = String(formData.salesPrice).replace(/,/g, "");
    const value = Number(formatted);
    if (!value) {
      handleUpdateToaster({
        message: "Failure! Please enter a Seller Must Net",
        visible: true,
        type: "error",
      });
    }
    return !isNaN(value) && value !== 0;
  };

  // Reusable modal rendering
  const renderModal = (currentStep) => (
    <ResultsModal
      step={currentStep}
      setStep={(newStep) => {
        // setStep(newStep);
        openModal({
          title: "What would you like to do next?",
          size: "smallmodel",
          content: renderModal(newStep),
        });
      }}
      closeModal={closeModal}
      onEditManual={() => {
        // Close the results modal and show the manual editor in the main screen
        closeAllModals();
        setShowManualEditor(true);
      }}
      // pass current RHF snapshot under `values` (preferred)
      values={getValues ? getValues() : {}}
      formatCurrency={formatCurrency}
      cancelModal={handleCancelModal}
      cancelResultModal={handleCancelResultModal}
      saveCalc={handleSave}
      handleDownloadPrintPDF={handleDownloadPrintPDF}
      checkValidSalesPrice={checkValidSalesPrice}
      handleSaveCalc={handleSave}
      getValues={getValues}
      showToaster={handleUpdateToaster}
      theme={theme}
    />
  );

  // ✅ Main calculation handler
  const handleCalculate = async () => {
    if (
      !getVal("sellerMustNet", "") ||
      Number(getVal("sellerMustNet", 0)) === 0
    ) {
      openModal({
        title: "Warnings",
        size: "smallmodel",
        content: (
          <div class="modelspace pplication-fe">
            <p class="model-desc-details notice-icon">
              You haven't entered a Seller Must Net.
              <br />
              Do you want to continue without a Seller Must Net?
            </p>
            <div className="frm-btn popup-2-btn">
              <button
                class="submit-btn btn-bg inpt-btn btn-discard"
                onClick={() => closeModal()}
              >
                Cancel
              </button>
              <button
                class="submit-btn btn-bg inpt-btn"
                onClick={() => {
                  closeModal();
                  runCalculation();
                }}
              >
                Continue
              </button>
            </div>
          </div>
        ),
      });

      return;
    }

    runCalculation();
  };

  const runCalculation = async () => {
    // If Seller Must Net is 0, skip the heavy calculation step but still
    // open the results summary modal (UI behavior).
    let shouldRunCalc = true;
    try {
      const sellerMustNetNum = parseCurrency(getVal("sellerMustNet", 0));
      shouldRunCalc = sellerMustNetNum > 0;
    } catch (e) {
      shouldRunCalc = false;
    }

    // Issue #3: reset the iteration counter for each new Calculate run.
    // Otherwise once it hits 10, subsequent runs can terminate immediately.
    try {
      setCounter(0);
    } catch (e) {
      // ignore
    }
    if (shouldRunCalc) {
      // Wait for the *entire* net-first iteration loop to finish so the results
      // modal opens with final/updated totals.
      const netFirstDonePromise = startAwaitNetFirstCompletion(20000);
      try {
        //console.log("handleCalculate: starting calculation");
        await handleSalesPriceBlur();
      } catch (e) {
        console.warn("handleCalculate: failed to handleSalesPriceBlur", e);
      }

      try {
        await netFirstDonePromise;
      } catch (e) {
        // Shouldn't happen (promise always resolves), but don't block modal.
      }
    }

    /* const stateid = currentValues?.addressMeta?.stateid || 0;
    const params1 = { stateid, type: "seller_proration", maincompanyid: 1 };
    const apiNat = await getNationalSettingService(params1); */

    // Open Step 1 (results summary)
    try {
      openModal({
        title: "Seller2Net Sheet Results",
        size: "halffull",
        content: renderModal(1),
      });
      // Mark as completed only when we actually ran the calculation.
      // If Seller Must Net is missing (shouldRunCalc=false), we should allow the user
      // to enter it in ManualSheetSection without being blocked by the "new calculation" prompt.
      setCalcCompleted(!!shouldRunCalc);
    } catch (e) {
      console.warn("handleCalculate: failed to open results modal", e);
    }
  };

  const resetSellerDefaults = () => {
    try {
      setValue("salesPrice", "0.00");
      setValue("mortgageAmount", "0.00");
      setValue("listingAgentSplit", "0.00");
      setValue("buyerAgentSplit", "0.00");
      setValue("loanInfoMeta.homeWarrantyAmount", "0.00");
      const prev = getValues ? getValues() : {};
      setValue("loanInfoMeta", {
        ...(prev?.loanInfoMeta || {}),
        annualPropertyTax: "0.00",
      });
    } catch (e) {
      // If setValue failed, log the error. There's no legacy state to fall back to.
      console.warn("resetSellerDefaults: failed to reset RHF values", e);
    }
    setTtbData({});
    setIsCorrectProperty(false);
    setUseAnnualPropertyTax(useAnnualPropertyTax);
    setUseMortgageBalance(false);
    setUseHomeWarranty(false);
    setShowPreviewFromChild(false);
  };

  console.log("get val ", getVal());

  const handleSave = async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const isValid = checkValidSalesPrice();
      if (!isValid) return;

      const payload = buildSellerNetPayload({
        getVal,
        user,
        payload: { companyId, mrsid: getValues("addressMeta")?.mrsid, optionType: "save" },
      });

      if (!loadedExistingRef.current) {
        const sellerCalc = await saveNetFirstCalc(payload);
        if (sellerCalc) {
          loadedExistingRef.current = sellerCalc.calculator.id;
          setValue("id", sellerCalc.calculator.id);
          closeModal();
          handleUpdateToaster({
            message: "Seller2Net Calculator saved successfully!",
            visible: true,
            type: "success",
          });
        }
      } else {
        payload.id = loadedExistingRef.current;
        openModal({
          title: "Warnings",
          size: "mediumsizewidth",
          content: (
            <div class="modelspace application-fe">
              <p class="model-desc-details notice-icon">
                A saved file already exist with same file name, do you want to:
              </p>
              <div className="frm-btn popup-2-btn">
                <button
                  class="submit-btn btn-bg inpt-btn"
                  onClick={async () => {
                    await updateExisitingCalc(payload);
                  }}
                >
                  Overwrite Exisiting
                </button>

                <button
                  class="submit-btn btn-bg inpt-btn"
                  onClick={async () => {
                    await saveNewCalc();
                  }}
                >
                  Save as New File
                </button>

                <button
                  class="submit-btn btn-bg inpt-btn btn-discard"
                  onClick={() => closeModal()}
                >
                  Cancel
                </button>
              </div>
            </div>
          ),
        });
      }
    } catch (error) {
      console.warn("Error while Saving the file", error);
    }
  };

  const updateExisitingCalc = async (payload) => {
    try {
      const sellerCalc = await updateNetFirstCalc(payload);
      if (sellerCalc) {
        closeModal();
        handleUpdateToaster({
          message: "Seller2Net Calculator updated successfully!",
          visible: true,
          type: "success",
        });
      }
    } catch (error) {
      console.warn("Error while updating the file", error);
    }
  };

  const saveNewCalc = async () => {
    try {
      loadedExistingRef.current = null;
      await handleSave();
      closeModal();
    } catch (error) {
      console.warn("Error while Saving the file", error);
    }
  };

  const handleNewCalc = async (opts = {}) => {
    const forceFull = !!opts?.forceFull;
    const forceQuick = !!opts?.forceQuick && !forceFull;

    // When starting a new calc in quick mode, keep the current address (per UX).
    const shouldQuick = forceQuick || (isQuickMode && !forceFull);
    let preservedAddressMeta = null;
    let preservedLoanInfoMeta = null;
    let preservedAnnualTax = null;
    let preservedSellerOtherCostMeta = null;
    let preservedTemplateSellerOtherCosts = null;
    let preservedDiscountRate = null;
    if (shouldQuick) {
      try {
        preservedAddressMeta =
          (typeof getValues === "function" ? getValues("addressMeta") : null) || null;
      } catch (e) {
        preservedAddressMeta = null;
      }

      // Preserve the user's discount rate (but NOT the discount amount).
      try {
        const currentOtherCosts =
          (typeof getValues === "function" ? getValues("otherCostMeta") : []) || [];
        if (Array.isArray(currentOtherCosts) && currentOtherCosts.length) {
          const discountRow = currentOtherCosts.find((it) => {
            const key = (it?.key || it?.camelKey || "")
              .toString()
              .toLowerCase();
            return key === "discount";
          });
          if (discountRow && typeof discountRow.rate !== "undefined") {
            preservedDiscountRate = discountRow.rate;
          }
        }
      } catch (e) {
        /* ignore */
      }
      try {
        if (
          preservedAddressMeta &&
          typeof preservedAddressMeta === "object" &&
          Object.keys(preservedAddressMeta).length
        ) {
          sessionStorage.setItem(
            PRESERVED_ADDRESS_STORAGE_KEY,
            JSON.stringify(preservedAddressMeta)
          );
        }
      } catch (e) {
        /* ignore */
      }
      //keep loan info
      try {
        if (typeof getValues === "function") {
          preservedLoanInfoMeta = getValues("loanInfoMeta") || null;
          if (preservedLoanInfoMeta && typeof preservedLoanInfoMeta === "object") {
            // IMPORTANT: loanInfoMeta.amount drives DiscountFields discount amount.
            // For a new calculator, we preserve loan-to-be-paid inputs but reset derived amounts.
            preservedLoanInfoMeta = {
              ...(preservedLoanInfoMeta || {}),
              amount: 0,
              adjusted: 0,
            };
            sessionStorage.setItem(
              PRESERVED_LOAN_INFO_STORAGE_KEY,
              JSON.stringify(preservedLoanInfoMeta)
            );
          }
        }
      } catch (e) {
        /* ignore */
      }
      //keep annual property tax
      try {
        const amount =
          (typeof getValues === "function"
            ? getValues("loanInfoMeta.annualPropertyTaxAmount")
            : undefined) ??
          (typeof getValues === "function"
            ? getValues("loanInfoMeta.annualPropertyTax")
            : undefined);
        preservedAnnualTax = { enabled: !!useAnnualPropertyTax, amount };
        sessionStorage.setItem(
          PRESERVED_ANNUAL_PROPERTY_TAX_STORAGE_KEY,
          JSON.stringify(preservedAnnualTax)
        );
      } catch (e) {
        /* ignore */
      }

      // Preserve sellerOtherCostMeta (this is the parsedSellerOtherCosts array used by ManualSheet).
      try {
        preservedSellerOtherCostMeta =
          (typeof getValues === "function" ? getValues("sellerOtherCostMeta") : null) ||
          null;
        if (Array.isArray(preservedSellerOtherCostMeta) && preservedSellerOtherCostMeta.length) {
          sessionStorage.setItem(
            PRESERVED_SELLER_OTHER_COSTS_STORAGE_KEY,
            JSON.stringify(preservedSellerOtherCostMeta)
          );
        }
      } catch (e) {
        /* ignore */
      }

      // Preserve templateSellerOtherCosts (used for loan-type switching logic).
      try {
        preservedTemplateSellerOtherCosts =
          (typeof getValues === "function"
            ? getValues("templateSellerOtherCosts")
            : null) || null;
        if (
          preservedTemplateSellerOtherCosts &&
          typeof preservedTemplateSellerOtherCosts === "object" &&
          Object.keys(preservedTemplateSellerOtherCosts).length
        ) {
          sessionStorage.setItem(
            PRESERVED_TEMPLATE_SELLER_OTHER_COSTS_STORAGE_KEY,
            JSON.stringify(preservedTemplateSellerOtherCosts)
          );
        }
      } catch (e) {
        /* ignore */
      }
    }

    // starting a new calc should unlock editing
    try {
      setCalcCompleted(false);
      startNewCalcPromptOpenRef.current = false;
    } catch (e) {
      /* ignore */
    }

    setTtbData({});
    setIsCorrectProperty(
      !!(
        preservedAddressMeta &&
        typeof preservedAddressMeta === "object" &&
        Object.keys(preservedAddressMeta).length
      )
    );
    // Preserve annual-property-tax preference for quick-mode new calc.
    if (shouldQuick && preservedAnnualTax && typeof preservedAnnualTax.enabled !== "undefined") {
      setUseAnnualPropertyTax(!!preservedAnnualTax.enabled);
    } else {
      setUseAnnualPropertyTax(useAnnualPropertyTax);
    }
    setUseMortgageBalance(false);
    setUseHomeWarranty(false);
    setShowPreviewFromChild(false);
    setIsInitialLoad(false);
    setShowManualEditor(false);
    // Reset RHF form to canonical initial values so the UI behaves like a fresh form
    try {
      // reset will update field arrays to match initialValues as well
      if (typeof formMethods?.reset === "function")
        formMethods.reset(initialValues);

      // Ensure sellerMustNet starts blank (not 0)
      try {
        if (typeof setValue === "function") setValue("sellerMustNet", 0);
      } catch (e) {
        /* ignore */
      }

      // Restore preserved address into the new quick-mode calc.
      try {
        if (
          shouldQuick &&
          preservedAddressMeta &&
          typeof preservedAddressMeta === "object" &&
          Object.keys(preservedAddressMeta).length &&
          typeof setValue === "function"
        ) {
          setValue("addressMeta", preservedAddressMeta);
        }
      } catch (e) {
        /* ignore */
      }

      // Restore preserved loan info into the new quick-mode calc.
      try {
        if (
          shouldQuick &&
          preservedLoanInfoMeta &&
          typeof preservedLoanInfoMeta === "object" &&
          typeof setValue === "function"
        ) {
          const currentLoan =
            (typeof getValues === "function" ? getValues("loanInfoMeta") : {}) || {};
          setValue("loanInfoMeta", { ...currentLoan, ...preservedLoanInfoMeta });

          // Defensive: ensure derived values stay reset even if something merges over them.
          try {
            setValue("loanInfoMeta.amount", 0);
            setValue("loanInfoMeta.adjusted", 0);
          } catch (e) {
            /* ignore */
          }
        }
      } catch (e) {
        /* ignore */
      }

      // Restore annual property tax amount into RHF (if we captured one).
      try {
        if (
          shouldQuick &&
          preservedAnnualTax &&
          typeof preservedAnnualTax.amount !== "undefined" &&
          typeof setValue === "function"
        ) {
          setValue("loanInfoMeta.annualPropertyTaxAmount", preservedAnnualTax.amount);
          setValue("loanInfoMeta.annualPropertyTax", preservedAnnualTax.amount);
        }
      } catch (e) {
        /* ignore */
      }

      // Reset internal state used by net-first iteration
      try {
        initialSellerMustRef.current = 0;
        setInitialSellerMust(0);
      } catch (e) {
        /* ignore */
      }
      try {
        maintainAmountRef.current = 0;
        setMaintainAmount(0);
      } catch (e) {
        /* ignore */
      }
      try {
        setCounter(0);
      } catch (e) {
        /* ignore */
      }

      // Clear server/load-related refs and flags
      loadedExistingRef.current = null;
      isPopulatingRef.current = false;
      // After clearing the form, attempt to reload the user's template so the fresh form isn't empty
      try {
        if (typeof loadUserTemplate === "function" && mainId && userId) {
          await loadUserTemplate(mainId, userId, user?.companyid, {
            replaceSellerClosingCost: sellerClosingCostArray.replace,
            replaceClosingCostsSection2: closingCostsSection2Array.replace,
            replaceSellerOtherCost: sellerOtherCostArray.replace,
            replaceCompensation: compensationArray.replace,
            replaceOtherCost: otherCostArray.replace,
            setValue,
            calcId: null,
            isPopulatingRef,
          });
        }
      } catch (e) {
        console.warn(
          "handleNewCalc: failed to load user template after reset",
          e
        );
      }

      // Restore preserved seller-other-costs/template lookup after reset.
      // This is especially important for the "start new calculation" popup flow.
      try {
        if (
          shouldQuick &&
          Array.isArray(preservedSellerOtherCostMeta) &&
          preservedSellerOtherCostMeta.length
        ) {
          if (sellerOtherCostArray && typeof sellerOtherCostArray.replace === "function") {
            sellerOtherCostArray.replace(preservedSellerOtherCostMeta);
          } else if (typeof setValue === "function") {
            setValue("sellerOtherCostMeta", preservedSellerOtherCostMeta);
          }
        }
      } catch (e) {
        /* ignore */
      }
      try {
        if (
          shouldQuick &&
          preservedTemplateSellerOtherCosts &&
          typeof preservedTemplateSellerOtherCosts === "object" &&
          Object.keys(preservedTemplateSellerOtherCosts).length &&
          typeof setValue === "function"
        ) {
          setValue("templateSellerOtherCosts", preservedTemplateSellerOtherCosts);
        }
      } catch (e) {
        /* ignore */
      }

      // Preserve discount RATE but reset discount AMOUNT to 0 for the new calculator.
      // DiscountFields computes the discount amount from loanInfoMeta.amount; since we
      // reset loanInfoMeta.amount to 0 above, we also explicitly zero out the value/amount.
      try {
        const currentOtherCosts =
          (typeof getValues === "function" ? getValues("otherCostMeta") : []) || [];
        if (Array.isArray(currentOtherCosts) && currentOtherCosts.length) {
          const nextOtherCosts = currentOtherCosts.map((it) => {
            const key = (it?.key || it?.camelKey || "").toString().toLowerCase();
            if (key !== "discount") return it;
            return {
              ...(it || {}),
              // keep the user's discount rate (when available)
              ...(typeof preservedDiscountRate !== "undefined" && preservedDiscountRate !== null
                ? { rate: preservedDiscountRate }
                : {}),
              // reset computed amount/value
              value: "0.00",
              amount: 0,
            };
          });

          if (otherCostArray && typeof otherCostArray.replace === "function") {
            otherCostArray.replace(nextOtherCosts);
          } else if (typeof setValue === "function") {
            setValue("otherCostMeta", nextOtherCosts);
          }
        }
      } catch (e) {
        /* ignore */
      }
    } catch (e) {
      console.warn("handleNewCalc: failed to fully reset form", e);
    }

    // Replace history so the previous `/seller/:id` isn't kept in the back stack
    try {
      const target = forceQuick
        ? "/seller2net?mode=quick"
        : forceFull
          ? "/seller2net"
          : isQuickMode
            ? "/seller2net?mode=quick"
            : "/seller2net";
      navigate(target, {
        replace: true,
        state:
          shouldQuick &&
            preservedAddressMeta &&
            typeof preservedAddressMeta === "object" &&
            Object.keys(preservedAddressMeta).length
            ? { preservedAddressMeta }
            : undefined,
      });
    } catch (e) {
      // fallback: reload if navigate fails for some reason
      try {
        navigate(0);
      } catch (err) {
        /* ignore */
      }
    }
  };

  const handleUpdateToaster = ({ message, visible, type = "success" }) => {
    setToast({ message, visible, type });
    setTimeout(
      () => setToast({ message: "", visible: false, type: "success" }),
      5000
    );
  };

  return (
    <div className="calculater-layout">
      <div>
        <div className="back-inner">
          {!showManualEditor && (
            <CommonHeader
              title={`Seller2Net Sheet`}
              subTitle={`Enter the property address and seller must net to get started.`}
            />
          )}
        </div>
      </div>
      <div
        ref={containerRef}
        className={`calc-container ${showManualEditor === true ? "calc-page" : " double-height"
          }`}
      >
        <div className="clc-mid-contain">
          {!showManualEditor && showAddressView && !calcId && !isQuickMode && (
            <AddressSearchPreview
              control={control}
              openModal={openModal}
              closeModal={closeModal}
              onEnriched={async (data) => {
                const { ttb, payload, raw, showPreview, showSiteUnit } = data || {};

                const oldmrsid = getValues("addressMeta")?.mrsid || "";
                const timestamp = Date.now();

                // If no property data came back (ttb & raw are null or empty)
                const propertyUnavailable =
                  (!ttb || Object.keys(ttb || {}).length === 0) &&
                  (!raw || Object.keys(raw || {}).length === 0);

                if (propertyUnavailable) {
                  console.warn(
                    "AddressSearchPreview: Property not available — resetting default values"
                  );

                  // Reset all core fields
                  resetSellerDefaults();

                  // Optional visual feedback for user
                  alert("Property not available. Values have been reset.");

                  return; // Stop further processing
                }

                if (ttb) setTtbData(ttb);
                if (raw?.data?.companyid) {
                  //setCompanyId(raw.data.companyid);
                }
                const companyIdVal =
                  (raw && raw.data && raw.data.companyid) ||
                  raw?.companyid ||
                  payload?.companyid ||
                  null;
                if (companyIdVal && typeof getUserTemplate === "function") {
                  try {
                    const data = await getUserTemplate(companyIdVal);
                    // treat null/undefined/empty object/empty array as an empty response
                    const isEmpty =
                      data == null ||
                      (Array.isArray(data) && data.length === 0) ||
                      (typeof data === "object" &&
                        !Array.isArray(data) &&
                        Object.keys(data).length === 0);
                    setTemplateMissing(false);
                    if (isEmpty) {
                      if (typeof handleUpdateToaster === "function") {
                        if (
                          Array.isArray(data) ||
                          (typeof data === "object" &&
                            Object.keys(data).length === 0)
                        ) {
                          try {
                            handleUpdateToaster({
                              message:
                                "The zip code you entered is incorrect or the State/County is not supported.",
                              visible: true,
                              type: "error",
                            });
                            setTemplateMissing(true);
                          } catch (e) { }
                          //return;
                        }
                      } else {
                        // no-op: do not set or clear `templateError` here when toaster unavailable
                      }
                    }
                  } catch (err) {
                    console.warn("getUserTemplate failed", err);
                  }
                } else {
                  // clear any previous template error if no companyId present
                }
                if (payload) {
                  const addDays = (date, days) => {
                    const d = new Date(date);
                    d.setDate(d.getDate() + days);
                    return d;
                  };
                  const toDateStr = (d) =>
                    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
                      2,
                      "0"
                    )}-${String(d.getDate()).padStart(2, "0")}`;
                  const defaultEst = toDateStr(addDays(new Date(), 30));
                  try {
                    const prev = getValues ? getValues() : {};
                    // Build a human-readable addressMeta from payload if available
                    const streetNo = payload.street_no || "";
                    const route = payload.route_long || payload.route || "";
                    const siteUnit = payload.site_unit || "";
                    const formattedAddress = payload.formatted_address || "";
                    //const formattedAddress = (streetNo || route) ? `${streetNo ? streetNo + ' ' : ''}${route}${siteUnit ? ' #' + siteUnit : ''}` : (prev?.addressMeta && typeof prev.addressMeta === 'string' ? prev.addressMeta : (prev?.addressMeta?.addressMeta || ''));
                    //console.log("ttb:", ttb);
                    setValue("loanInfoMeta.estimatedClosingDate", defaultEst);
                    setValue("loanInfoMeta", {
                      ...(prev?.loanInfoMeta || {}),
                      annualPropertyTax: ttb?.taxAmount,
                    });
                    setValue("loanInfoMeta", {
                      ...(prev?.loanInfoMeta || {}),
                      annualPropertyTaxAmount: ttb?.taxAmount,
                    });
                    setValue("addressMeta", {
                      address: formattedAddress,
                      street_no: streetNo,
                      street_name: route,
                      county:
                        ttb?.county_name || prev?.addressMeta?.county || "",
                      closing_state:
                        ttb?.state_name || prev?.address?.state || "",
                      closing_county:
                        ttb?.county_name || prev?.addressMeta?.county || "",
                      closing_zip_code: "",
                      city: payload?.city || prev?.addressMeta?.city || "",
                      state:
                        ttb?.state_code ||
                        payload?.state ||
                        prev?.addressMeta?.state ||
                        "",
                      countyid:
                        ttb?.countyid ||
                        payload?.countyid ||
                        prev?.addressMeta?.countyid ||
                        "",
                      stateid:
                        ttb?.stateid ||
                        payload?.stateid ||
                        prev?.addressMeta?.stateid ||
                        "",
                      zip: payload?.zip || prev?.addressMeta?.zip || "",
                      addressIncludesUnit:
                        showSiteUnit ||
                        prev?.addressMeta?.addressIncludesUnit ||
                        "",
                      unitNumber:
                        siteUnit || prev?.addressMeta?.unitNumber || "",
                      mrsid: oldmrsid ? oldmrsid : `${timestamp}`,
                    });
                    setValue("main_company_name", main_company_name || "");
                    setValue &&
                      setValue(
                        "loanInfoMeta.loansToBePaidBalance1",
                        ttb?.loanBalance?.balance
                      );
                    setValue &&
                      setValue(
                        "loanInfoMeta.loansToBePaidRate1",
                        ttb?.loanBalance?.interestRate
                      );
                  } catch (e) {
                    // Failed to write address enrichment into RHF — log and continue.
                    console.warn(
                      "Address enrichment: failed to set RHF values",
                      e
                    );
                  }
                }
                setShowPreviewFromChild(Boolean(showPreview));
                setIsInitialLoad(Boolean(showPreview));
              }}
              // ✅ handle manual typing too
              onAddressEdited={(val) => {
                if (val && val.length > 3) {
                  setIsCorrectProperty(true); // Reset confirmation when user changes or reselects addressMeta
                  setIsInitialLoad(false);
                  setUseAnnualPropertyTax(useAnnualPropertyTax);
                  setUseMortgageBalance(true);
                  setUseHomeWarranty(false);
                }
              }}
            />
          )}

          {showPreviewFromChild &&
            !showManualEditor &&
            !calcId &&
            !templateMissing &&
            !isQuickMode && (
              <PropertyDetailsSection
                ttbData={ttbData}
                formatCurrency={formatCurrency}
                salesPriceError={salesPriceError}
                mortgageAmountError={mortgageAmountError}
                isCorrectProperty={isCorrectProperty}
                isInitialLoad={isInitialLoad}
                setIsCorrectProperty={setIsCorrectProperty}
                setIsInitialLoad={setIsInitialLoad}
                useAnnualPropertyTax={useAnnualPropertyTax}
                setUseAnnualPropertyTax={setUseAnnualPropertyTax}
                useMortgageBalance={useMortgageBalance}
                setUseMortgageBalance={setUseMortgageBalance}
                useHomeWarranty={useHomeWarranty}
                setUseHomeWarranty={setUseHomeWarranty}
                getValues={getValues}
                setValue={setValue}
                handleCurrencyBlur={handleCurrencyBlur}
                control={control}
                cleanNumber={cleanNumber}
                watch={watch}
                handleCalculate={handleCalculate}
              />
            )}
        </div>

        {showManualEditor && (
          // When the user chooses to edit the manual sheet, show it directly in the page
          <ManualSheetSection
            FloatingInput={FloatingInput}
            FloatingNumber={FloatingNumber}
            CustomSelect={CustomSelect}
            formatCurrency={formatCurrency}
            salesPriceError={salesPriceError}
            mortgageAmountError={mortgageAmountError}
            navigate={navigate}
            isCorrectProperty={isCorrectProperty}
            useAnnualPropertyTax={useAnnualPropertyTax}
            setUseAnnualPropertyTax={setUseAnnualPropertyTax}
            useMortgageBalance={useMortgageBalance}
            setUseMortgageBalance={setUseMortgageBalance}
            handleCurrencyBlur={handleCurrencyBlur}
            onSalesPriceBlur={handleSalesPriceBlur}
            checkValidSalesPrice={checkValidSalesPrice}
            handleDownloadPrintPDF={handleDownloadPrintPDF}
            control={control}
            watch={watch}
            setValue={setValue}
            getValues={getValues}
            sellerClosingCostArray={sellerClosingCostArray}
            closingCostsSection2Array={closingCostsSection2Array}
            sellerOtherCostArray={sellerOtherCostArray}
            compensationArray={compensationArray}
            otherCostArray={otherCostArray}
            // allow the manual sheet to request being closed
            // Manual-sheet "New" should always open the non-quick calculator.
            onClose={(opts) => {
              handleNewCalc({ ...(opts || {}), forceFull: true });
            }}
            cleanNumber={cleanNumber}
            handleSellerCalculation={handleSellerCalculation}
            ttbData={ttbData}
            handleSaveCalc={handleSave}
            showToaster={handleUpdateToaster}
            setCompanyId={setCompanyId}
            templateMissing={templateMissing}
            calcCompleted={calcCompleted}
            onAttemptEditAfterComplete={openStartNewCalculationPrompt}
            onCalculationComplete={() => {
              try {
                setCalcCompleted(true);
              } catch (e) {
                /* ignore */
              }
            }}
          />
        )}
      </div>

      <Toast
        message={toast.message}
        type={toast.type}
        visible={toast.visible}
      />
    </div>
  );
};

export default SellerNetSheet;
